class ProductSizePicker extends HTMLElement {
  /** @type {Array<{id: number, title?: string, option1: string, option2: string, option3: string, available: boolean}> | null} */
  #variants = null;

  connectedCallback() {
    this.#variants = this.#parseVariants();
    this.addEventListener('click', this.#onClick);

    const formElement = this.#getFormElement();
    formElement?.addEventListener('submit', this.#onFormSubmit, true);

    this.#gatePurchaseButtons();
  }

  disconnectedCallback() {
    const formElement = this.#getFormElement();
    formElement?.removeEventListener('submit', this.#onFormSubmit, true);
  }

  #parseVariants() {
    const raw = this.dataset.variants;
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  #normalizeSize(value) {
    return String(value || '').trim();
  }

  #matchesSize(value, target) {
    const normalized = this.#normalizeSize(value);
    if (!normalized || normalized === 'Default Title') return false;
    if (normalized === target) return true;

    if (!/^\d+$/.test(target)) return false;

    return normalized.split(/[\s/\-–|]+/).some((part) => this.#normalizeSize(part) === target);
  }

  #findVariant(size) {
    const target = this.#normalizeSize(size);
    if (!target || !this.#variants?.length) return null;

    return (
      this.#variants.find((variant) => {
        const fields = [variant.option1, variant.option2, variant.option3, variant.title];
        return fields.some((value) => this.#matchesSize(value, target));
      }) || null
    );
  }

  #resolveVariantForCart(size) {
    const matched = this.#findVariant(size);
    if (matched) return matched;
    if (this.#variants?.length === 1) return this.#variants[0];
    return null;
  }

  #getProductForm() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('product-form-component');
  }

  #getFormElement() {
    return this.#getProductForm()?.querySelector('form') ?? null;
  }

  #getSelectedSizeButton() {
    const selected = this.querySelector('[data-size].is-selected');
    return selected instanceof HTMLButtonElement ? selected : null;
  }

  #setSizeProperty(formElement, size) {
    let sizePropertyInput = formElement.querySelector('input[name="properties[Size]"]');
    if (!(sizePropertyInput instanceof HTMLInputElement)) {
      sizePropertyInput = document.createElement('input');
      sizePropertyInput.type = 'hidden';
      sizePropertyInput.name = 'properties[Size]';
      formElement.appendChild(sizePropertyInput);
    }
    sizePropertyInput.value = size;
  }

  #syncFormSelection(size) {
    const formElement = this.#getFormElement();
    if (!formElement) return false;

    const variant = this.#resolveVariantForCart(size);
    const variantInput = formElement.querySelector('input[name="id"]');

    if (!(variantInput instanceof HTMLInputElement) || !variant) {
      return false;
    }

    variantInput.value = String(variant.id);
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#setSizeProperty(formElement, size);

    return true;
  }

  #gatePurchaseButtons() {
    const form = this.#getProductForm();
    if (!form) return;

    form.querySelector('add-to-cart-component')?.disable?.();
    this.#hideBuyNow(form);
  }

  #hideBuyNow(form) {
    form.querySelector('.accelerated-checkout-block')?.setAttribute('hidden', 'true');
  }

  #showBuyNow(form) {
    form.querySelector('.accelerated-checkout-block')?.removeAttribute('hidden');
  }

  #enablePurchaseButtons(form, size, variantId) {
    this.#syncFormSelection(size);

    const addToCart = form.querySelector('add-to-cart-component');
    const addToCartLabel = addToCart?.querySelector('.add-to-cart-text__content span span');
    if (addToCartLabel) {
      addToCartLabel.textContent = this.dataset.addToCartLabel || 'Add to cart';
    }

    addToCart?.enable?.();
    this.#showBuyNow(form);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());
  }

  #onFormSubmit = () => {
    const selectedButton = this.#getSelectedSizeButton();
    const size = selectedButton?.dataset.size;
    if (!size) return;

    this.#syncFormSelection(size);
  };

  #onClick = (event) => {
    const button = event.target.closest('[data-size]');
    if (!(button instanceof HTMLButtonElement)) return;

    const size = button.dataset.size;
    if (!size) return;

    const variant = this.#resolveVariantForCart(size);
    if (!variant) return;

    this.querySelectorAll('[data-size]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const form = this.#getProductForm();
    if (!form) return;

    this.#enablePurchaseButtons(form, size, String(variant.id));
  };
}

if (!customElements.get('product-size-picker')) {
  customElements.define('product-size-picker', ProductSizePicker);
}
